import React, { lazy, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import PaymentIcon from '@mui/icons-material/Payment';
import MapIcon from '@mui/icons-material/Map';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import GroupIcon from '@mui/icons-material/Group';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  AddAlert,
  Announcement,
  History,
  LiveHelp,
  Payment,
  Room,
  Settings,
  SportsEsports,
  BarChart,
  LocationCity,
  Tab,
  StickyNote2,
  Reviews,
  ElectricScooter,
  DirectionsCar,
  BusinessCenter,
  Campaign,
  AdminPanelSettings,
  Tune,
  Build,
  Schedule,
  Layers,
  Place,
  Security,
  SearchOff,
  Inventory,
  Engineering,
  Category,
  Dashboard,
  BatteryChargingFull,
  Assignment,
  QrCode2,
  EmojiEmotions,
  Web,
  SettingsSuggest,
} from '@mui/icons-material';
import { checkPermission } from '../../resources/functions';

// Сторінки підвантажуються окремими чанками: інакше весь код усіх
// сторінок лежить в одному файлі й вантажиться навіть тому, хто
// відкрив лише карту.
const BonusGamePage = lazy(() => import('../../pages/BonusGamePage'));
const RewardsPage = lazy(() => import('../../pages/RewardsPage'));
const FaqPage = lazy(() => import('../../pages/FAQPage'));
const CitiesBalancePage = lazy(() => import('../../pages/CitiesBalancePage'));
const ScootersLockHistoryPage = lazy(() => import('../../pages/ScootersLockHistoryPage'));
const ZonesPage = lazy(() => import('../../pages/ZonesPage'));
const ScootersPage2 = lazy(() => import('../../pages/ScootersPage2'));
const TripsPage2 = lazy(() => import('../../pages/TripsPage2'));
const PromocodesPage = lazy(() => import('../../pages/PromocodesPage'));
const UsersPage = lazy(() => import('../../pages/UsersPage'));
const PushesPage = lazy(() => import('../../pages/PushesPage'));
const NoticesPage2 = lazy(() => import('../../pages/NoticesPage2'));
const NotesPage = lazy(() => import('../../pages/NotesPage'));
const SparePartsPage = lazy(() => import('../../pages/SparePartsPage'));
const WorkshopTasksPage = lazy(() => import('../../pages/WorkshopTasksPage'));
const DriverTasksPage = lazy(() => import('../../pages/DriverTasksPage/DriverTasksPage'));
const BatteriesPage = lazy(() => import('../../pages/BatteriesPage/BatteriesPage'));
const TariffsPage = lazy(() => import('../../pages/TariffsPage'));
const HistoryPage = lazy(() => import('../../pages/HistoryPage'));
const CitiesPage = lazy(() => import('../../pages/CitiesPage'));
const AdminsPage = lazy(() => import('../../pages/AdminsPage'));
const AdminRolesPage = lazy(() => import('../../pages/AdminRolesPage'));
const TabsPage = lazy(() => import('../../pages/TabsPage/TabsPage'));
const StatisticPage = lazy(() => import('../../pages/StatisticPage/StatisticPage'));
const NewsPage = lazy(() => import('../../pages/NewsPage'));
const TrackitPage = lazy(() => import('../../pages/TrackitPage'));
const SubscriptionPage = lazy(() => import('../../pages/SubscriptionPage'));
const FeedbacksPage = lazy(() => import('../../pages/FeedbacksPage'));
const WorkingHoursPage = lazy(() => import('../../pages/WorkingHoursPage'));
const ZoneManagementPage = lazy(() => import('../../pages/ZoneManagementPage'));
const MapMarkersPage = lazy(() => import('../../pages/MapMarkersPage'));
const LostScootersPage = lazy(() => import('../../pages/LostScootersPage'));
const ScooterTypesPage = lazy(() => import('../../pages/ScooterTypesPage'));
const WorkShiftsPage = lazy(() => import('../../pages/WorkShiftsPage/WorkShiftsPage'));
const QrGeneratorPage = lazy(() => import('../../pages/QrGeneratorPage'));
const AvatarsPage = lazy(() => import('../../pages/AvatarsPage'));
const AppModalAlertsPage = lazy(() => import('../../pages/AppModalAlertsPage'));
const SystemSettingsPage = lazy(() => import('../../pages/SystemSettingsPage'));

export interface Route {
  path: string;
  label?: string;
  code?: string;
  component?: (props: any) => JSX.Element | null;
  nested?: Route[];
  icon?: ReactElement;
}

const useRoutes = (): Route[] => {
  const auth = useSelector(selectAuth);
  const admin = useSelector(selectAdmins);

  if (!auth.info) return [];

  const links: Route[] = [
    {
      label: 'Дашборд',
      path: '/admin/stats',
      component: StatisticPage,
      code: 'statistics',
      icon: <Dashboard color='inherit' />
    },
    {
      label: 'Управління',
      path: '/control',
      icon: <BusinessCenter color='inherit' />,
      nested: [
        {
          label: 'Карта',
          icon: <MapIcon color='inherit' />,
          path: '/control',
          code: 'control',
          component: ZonesPage
        },
        {
          label: 'Самокати',
          icon: <ElectricScooter color='inherit' />,
          path: '/scooters-2',
          code: 'scooters',
          component: ScootersPage2
        },
        {
          label: 'Поїздки',
          icon: <DirectionsCar color='inherit' />,
          path: '/trips-2',
          code: 'contracts',
          component: TripsPage2
        },
        {
          label: 'Повідомлення',
          icon: <NotificationsActiveIcon color='inherit' />,
          path: '/notices-2',
          code: 'notices',
          component: NoticesPage2
        },
        {
          label: 'Історія блок/розблок',
          icon: <History color='inherit' />,
          path: '/scooters-lock-history',
          code: 'scooters-lock-history',
          component: ScootersLockHistoryPage
        },
        {
          label: 'Користувачі',
          icon: <GroupIcon color='inherit' />,
          path: '/users',
          code: 'users',
          component: UsersPage
        },
        {
          label: 'Відгуки',
          icon: <Reviews color='inherit' />,
          path: '/feedbacks',
          code: 'contractReviews',
          component: FeedbacksPage
        },
        {
          label: 'Trackit',
          icon: <Room color='inherit' />,
          path: '/trackit',
          code: 'trackit',
          component: TrackitPage
        },
        {
          label: 'Загублені самокати',
          icon: <SearchOff color='inherit' />,
          path: '/lost-scooters',
          code: 'lost-scooters',
          component: LostScootersPage
        },
        {
          label: 'Завдання',
          icon: <Assignment color='inherit' />,
          path: '/driver-tasks',
          code: 'driver_tasks',
          component: DriverTasksPage
        }
      ]
    },
    {
      label: 'Маркетинг',
      path: '/promocodes',
      icon: <Campaign color='inherit' />,
      nested: [
        {
          label: 'Промокоди',
          icon: <CardGiftcardIcon color='inherit' />,
          path: '/promocodes',
          code: 'bonuscodes',
          component: PromocodesPage
        },
        {
          label: 'Абонементи',
          icon: <CardMembershipIcon color='inherit' />,
          path: '/subscriptions',
          code: 'tariffs',
          component: SubscriptionPage
        },
        {
          label: 'Винагороди',
          icon: <SportsEsports color='inherit' />,
          path: '/rewards',
          code: 'rewards',
          component: RewardsPage
        },
        {
          label: 'Пуш-повідомлення',
          icon: <AddAlert color='inherit' />,
          path: '/pushes',
          code: 'pushes',
          component: PushesPage
        },
        {
          label: 'Новини',
          icon: <Announcement color='inherit' />,
          path: '/news',
          code: 'news',
          component: NewsPage
        },
        {
          label: 'Модалка в додатку',
          icon: <Web color='inherit' />,
          path: '/app-modal-alerts',
          // Таб modal_alerts у базі дає і меню, і CRUD-маршрути бекенду.
          code: 'modal_alerts',
          component: AppModalAlertsPage
        }
      ]
    },
    {
      label: 'Сервіс',
      path: '/notes',
      icon: <Build color='inherit' />,
      nested: [
        {
          label: 'Нотатки',
          icon: <StickyNote2 color='inherit' />,
          path: '/notes',
          code: 'scooter_notes',
          component: NotesPage
        },
        {
          label: 'Склад запчастин',
          icon: <Inventory color='inherit' />,
          path: '/spare-parts',
          code: 'spare_parts',
          component: SparePartsPage
        },
        {
          label: 'Задачі майстерні',
          icon: <Engineering color='inherit' />,
          path: '/workshop-tasks',
          code: 'workshop_tasks',
          component: WorkshopTasksPage
        },
        {
          label: 'Акумулятори',
          icon: <BatteryChargingFull color='inherit' />,
          path: '/batteries',
          code: 'batteries',
          component: BatteriesPage
        }
      ]
    }
  ];

  const isSuperAdmin = auth.info.type === 'superAdmin';
  const isAllReadOnly = auth.info.type === 'all_read_only' || admin.info?.type === 'all_read_only' || admin.info?.login === 'all_read_only';
  const isAminsCrud = checkPermission(admin.info?.tabs ?? [], 'admins crud');

  if (isSuperAdmin || isAllReadOnly || isAminsCrud) {
    links.push({
      label: 'Адміністрування',
      path: '/admin/cities',
      icon: <AdminPanelSettings color='inherit' />,
      nested: [
        {
          label: 'Міста',
          path: '/admin/cities',
          component: props => (
            <CitiesPage {...props} isSuperAdmin={isSuperAdmin} />
          ),
          icon: <LocationCity color='inherit' />
        },
        {
          label: 'Адміністратори',
          path: '/admin/admins',
          component: AdminsPage,
          icon: <GroupIcon color='inherit' />
        },
        ...(isSuperAdmin
          ? [
              {
                label: 'Ролі адміністраторів',
                path: '/admin/roles',
                component: AdminRolesPage,
                icon: <Security color='inherit' />
              },
              {
                label: 'Таби',
                path: '/admin/tabs',
                component: TabsPage,
                icon: <Tab color='inherit' />
              }
            ]
          : []),
        {
          label: 'Транзакції',
          icon: <History color='inherit' />,
          path: '/history',
          code: 'history',
          component: HistoryPage
        },
        ...((isSuperAdmin || auth.info.type === 'admin')
          ? [
              {
                label: 'Робочі зміни',
                path: '/admin/work-shifts',
                component: WorkShiftsPage,
                icon: <Schedule color='inherit' />
              }
            ]
          : [])
      ]
    });
  } else {
    links.push({
      label: 'Вибір міста',
      path: '/cities',
      component: CitiesPage,
      icon: <Settings color='inherit' />
    });
  }

  links.push({
    label: 'Налаштування',
    path: '/working-hours',
    icon: <Tune color='inherit' />,
    nested: [
      {
        label: 'Тарифи',
        icon: <PaymentIcon color='inherit' />,
        code: 'tariffs',
        path: '/tariffs',
        component: TariffsPage
      },
      {
        label: 'Робочі години',
        icon: <Schedule color='inherit' />,
        path: '/working-hours',
        code: 'working_hours',
        component: WorkingHoursPage
      },
      {
        label: 'Зони (редагування)',
        icon: <Layers color='inherit' />,
        path: '/zones-management',
        code: 'zone-edit',
        component: ZoneManagementPage
      },
      {
        label: 'Маркери на карті',
        icon: <Place color='inherit' />,
        path: '/map-markers',
        code: 'map_markers',
        component: MapMarkersPage
      },
      {
        label: 'Типи самокатів',
        icon: <Category color='inherit' />,
        path: '/scooter-types',
        code: 'scooter_types',
        component: ScooterTypesPage
      },
      // Тільки суперадмін: фільтр нижче пропускає all_read_only повз себе,
      // тому обмеження через code тут не спрацювало б.
      ...(isSuperAdmin
        ? [
            {
              label: 'Аватари користувачів',
              icon: <EmojiEmotions color='inherit' />,
              path: '/avatars',
              component: AvatarsPage
            },
            {
              label: 'Системні',
              icon: <SettingsSuggest color='inherit' />,
              path: '/system-settings',
              component: SystemSettingsPage
            }
          ]
        : []),
      {
        label: 'FAQ',
        icon: <LiveHelp color='inherit' />,
        path: '/faq',
        code: 'faq',
        component: FaqPage
      },
      {
        label: 'Генерація QR кодів',
        icon: <QrCode2 color='inherit' />,
        path: '/qr-generator',
        component: QrGeneratorPage
      }
    ]
  });

  if (auth.info.type !== 'superAdmin' && !isAllReadOnly) {
    return links.filter(link => {
      if (link.nested && link.nested.length > 0) {
        const hasAccessToAnyNested = link.nested.some(nestedLink => {
          if (!nestedLink.code) return true;
          return checkPermission(admin.info?.tabs || [], nestedLink.code);
        });
        return hasAccessToAnyNested;
      }
      
      if (link.code) {
        return checkPermission(admin.info?.tabs || [], link.code);
      }
      
      return true;
    }).map(link => {
      if (link.nested && link.nested.length > 0) {
        return {
          ...link,
          nested: link.nested.filter(nestedLink => {
            if (!nestedLink.code) return true;
            return checkPermission(admin.info?.tabs || [], nestedLink.code);
          })
        };
      }
      return link;
    });
  }

  return links;
};

export default useRoutes;
